import { addErrorActionContext } from './error-action-history.js';
import { ANALYTICS_BATCH_MAX_BYTES, ANALYTICS_BATCH_MAX_EVENTS, ANALYTICS_FLUSH_DELAY_MS } from '../shared/analytics-delivery.js';
import { isAnalyticsOptedOut } from './analytics-identity.js';

interface AnalyticsPayload {
  event: string;
  props: Record<string, string>;
  customer_grant_id?: string;
  customer_event_id?: string;
}

interface Delivery {
  enqueue(payload: AnalyticsPayload): void;
  flush(): void;
}

const deliveries = new WeakMap<Window, Map<string, Delivery>>();

function delivery(endpoint: string): Delivery {
  let endpoints = deliveries.get(window);
  if (!endpoints) {
    endpoints = new Map();
    deliveries.set(window, endpoints);
  }
  const existing = endpoints.get(endpoint);
  if (existing) return existing;
  const owner = window;
  type BatchState = { status: 'empty' } | { status: 'buffered'; events: string[]; bytes: number; timer: number };
  let batchState: BatchState = { status: 'empty' };
  let pageState: 'active' | 'hidden' = 'active';

  function flush(): void {
    const pending = batchState;
    if (pending.status === 'empty') return;
    batchState = { status: 'empty' };
    owner.clearTimeout(pending.timer);
    if (isAnalyticsOptedOut()) return;
    const batch = pending.events;
    const body = `{"events":[${batch.join(',')}]}`;
    try {
      if (navigator.sendBeacon?.(endpoint, new Blob([body], { type: 'application/json' }))) return;
    } catch { /* A rejected beacon can still use fetch. */ }
    try {
      void fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
    } catch { /* Analytics must not interrupt the product. */ }
  }

  const result: Delivery = {
    flush,
    enqueue(payload) {
      if (isAnalyticsOptedOut()) return;
      const encoded = JSON.stringify(payload);
      const size = new TextEncoder().encode(encoded).byteLength + 1;
      if (size + 13 > ANALYTICS_BATCH_MAX_BYTES) return;
      if (batchState.status === 'buffered' && batchState.bytes + size > ANALYTICS_BATCH_MAX_BYTES) flush();
      if (batchState.status === 'empty') batchState = { status: 'buffered', events: [], bytes: 13, timer: owner.setTimeout(flush, ANALYTICS_FLUSH_DELAY_MS) };
      batchState.events.push(encoded);
      batchState.bytes += size;
      if (pageState === 'hidden' || batchState.events.length >= ANALYTICS_BATCH_MAX_EVENTS || owner.document.visibilityState === 'hidden') flush();
    },
  };
  owner.addEventListener('click', (event) => {
    if (event.composedPath().some(target => target instanceof owner.HTMLAnchorElement && target.href)) flush();
  });
  owner.addEventListener('submit', flush);
  // Later pagehide listeners can still enqueue exit events after this flush.
  owner.addEventListener('pagehide', () => { pageState = 'hidden'; flush(); });
  owner.addEventListener('pageshow', () => { pageState = 'active'; });
  owner.document.addEventListener('visibilitychange', () => {
    if (owner.document.visibilityState === 'hidden') flush();
  });
  endpoints.set(endpoint, result);
  return result;
}

export function queueAnalyticsEvent(endpoint: string, payload: AnalyticsPayload): void {
  if (isAnalyticsOptedOut()) return;
  delivery(endpoint).enqueue({ ...payload, props: addErrorActionContext(payload.event, payload.props) });
}

export function flushAnalyticsEvents(endpoint = '/api/analytics'): void {
  deliveries.get(window)?.get(endpoint)?.flush();
}
